import React, { useCallback, useEffect, useMemo, useState } from 'react'
import moment from 'moment'
import { flightApi } from '../utils/axiosConfig'
import { AdvertisementType } from '@industronics/fids-utils'
import { FlightDetails } from '../interface/flight.details'
import SingleFlightBoarding from '../components/SingleFlightBoarding'
import DoubleFlightBoarding from '../components/DoubleFlightBoarding'
import { DeviceDetails } from '../interface/device.details'
import { useSocket } from '../context/SocketContext'
import AdvertisementDisplay from '../components/AdvertisementDisplay'
import { useAdSequence } from '../hooks/useAdSequence'
import { apiRoutes } from '../utils/apiRoutes'
import mahbLogo from '../assets/MAHB_LOGO.jpg'
import { ADS_URL } from '../utils/env-utils'
import ScrollingText from '../components/scrolling/ScrollingText'

const DEFAULT_IMAGES = ['/1_eng.bmp', '/1_bm.bmp']
const DEFAULT_SWITCH_INTERVAL = 10000 // 10 seconds

interface FlightBoardingPageProps {
  device: DeviceDetails
}

const FlightBoardingPage: React.FC<FlightBoardingPageProps> = ({ device }) => {
  const [flights, setFlights] = useState<FlightDetails[]>([])
  const [initialLoadDone, setInitialLoadDone] = useState(false)
  const [currentTime, setCurrentTime] = useState(moment())
  const [imgIndex, setImgIndex] = useState(0)

  const { socket } = useSocket()
  const { currentAd } = useAdSequence(device.advertisement)

  useEffect(() => {
    const id = setInterval(() => setCurrentTime(moment()), 1000)
    return () => clearInterval(id)
  }, [])

  useEffect(() => {
    const id = setInterval(() => setImgIndex(prev => (prev + 1) % DEFAULT_IMAGES.length), DEFAULT_SWITCH_INTERVAL)
    return () => clearInterval(id)
  }, [])

  const fetchFlights = useCallback(async () => {
    try {
      const res = await flightApi.get(apiRoutes.flightTrips.getFlightTrips)
      const data: FlightDetails[] = res.data.data ?? []
      setFlights(data)
      setInitialLoadDone(true)
    } catch (err) {
      console.error('Failed to fetch flights:', err)
      setInitialLoadDone(true)
    }
  }, [])

  useEffect(() => {
    fetchFlights()
  }, [fetchFlights])

  const handleFlightUpdate = useCallback(
    (flightData: FlightDetails) => {
      const { assignment } = device
      const isForThisGate =
        flightData.loungeCode === assignment ||
        flightData.oldLoungeCode === assignment
      if (!isForThisGate) return
      fetchFlights()
    },
    [device, fetchFlights],
  )

  useEffect(() => {
    if (!socket) return
    const register = () => {
      socket.off('flight.update', handleFlightUpdate)
      socket.on('flight.update', handleFlightUpdate)
    }
    socket.on('connect', register)
    if (socket.connected) register()
    return () => {
      socket.off('connect', register)
      socket.off('flight.update', handleFlightUpdate)
    }
  }, [socket, handleFlightUpdate]);

  useEffect(() => {
      if (!socket) return;
  
      const handleBulkUpdate = (payload: {
          trigger: string;
          iataCode: string;
          cityName: string;
          translations: { lang: string; value: string }[];
          updatedFlightCount: number;
      }) => {
          console.log(`✈️ Bulk flight update — trigger: ${payload.trigger}, iataCode: ${payload.iataCode}, affected: ${payload.updatedFlightCount}`);
  
          setFlights(prevFlights =>
              prevFlights.map(flight => {
                  const hasStation = flight.stations?.some(
                      (s: any) => s.airport === payload.iataCode
                  );
                  if (!hasStation) return flight;
  
                  return {
                      ...flight,
                      stations: flight.stations.map((station: any) => {
                          if (station.airport !== payload.iataCode) return station;
                          return {
                              ...station,
                              city: payload.cityName,
                              translations: payload.translations,
                          };
                      }),
                  };
              })
          );
      };
  
      socket.on('flights.station.update', handleBulkUpdate);
  
      return () => {
          socket.off('flights.station.update', handleBulkUpdate);
      };
  }, [socket]);

  useEffect(() => {
    if (!socket) return;
  
    const handleRemarkUpdate = (payload: {
      trigger: string;
      remarkCode: string;
      remarkName: string;
      airportLang: string;
      translations: { lang: string; value: string }[];
      updatedFlightCount: number;
    }) => {
      console.log(`✈️ Bulk remark update — trigger: ${payload.trigger}, remarkCode: ${payload.remarkCode}, affected: ${payload.updatedFlightCount}`);
  
      setFlights(prevFlights =>
        prevFlights.map(flight => {
          if (
            flight.remarkCode?.key !== payload.remarkCode &&
            flight.carouselRemarks?.key !== payload.remarkCode &&
            flight.checkInStatus?.key !== payload.remarkCode &&
            flight.currentStatus?.key !== payload.remarkCode
          ) return flight;
  
          // Backend already resolved: en + airport.language + all airlineLanguages + all stationLangs
          // Frontend filters down per-flight by its own stationLangs only
          const stationLangs = new Set(
            (flight.stations ?? []).flatMap((s: any) =>
              (s.translations ?? [])
                .filter((t: any) => t.lang !== 'en')
                .map((t: any) => t.lang)
            )
          );
  
          const filteredTranslations = payload.translations.filter(
            (t: any) => t.lang === 'en' || t.lang === flight?.airline?.language || t.lang === payload.airportLang || stationLangs.has(t.lang)
          );
  
          const patch: Partial<FlightDetails> = {};
  
          if (flight.remarkCode?.key === payload.remarkCode) {
            patch.remarkCode = { ...flight.remarkCode, description: payload.remarkName, translations: filteredTranslations };
          }
          if (flight.carouselRemarks?.key === payload.remarkCode) {
            patch.carouselRemarks = { ...flight.carouselRemarks, description: payload.remarkName, translations: filteredTranslations };
          }
          if (flight.checkInStatus?.key === payload.remarkCode) {
            patch.checkInStatus = { ...flight.checkInStatus, description: payload.remarkName, translations: filteredTranslations };
          }
          if (flight.currentStatus?.key === payload.remarkCode) {
            patch.currentStatus = { ...flight.currentStatus, description: payload.remarkName, translations: filteredTranslations };
          }
  
          return { ...flight, ...patch };
        })
      );
    };
  
    socket.on('flights.remark.update', handleRemarkUpdate);
  
    return () => {
      socket.off('flights.remark.update', handleRemarkUpdate);
    };
  }, [socket]);
  
    useEffect(() => {
      if (!socket) return;
  
      const handleRefetch = (payload: { trigger: string }) => {
          console.log(`🔄 Flights refetch triggered — reason: ${payload.trigger}`);
          fetchFlights();
      };
  
      socket.on('flights.refetch', handleRefetch);
      return () => { socket.off('flights.refetch', handleRefetch); };
  }, [socket]);

  // Non-destructive time-window filter — flights reappear automatically when they enter the window
  const windowedFlights = useMemo(() => {
    const timeWindowHours = device.cluster?.settings?.flightTimeWindowInHour;
    if (timeWindowHours == null || timeWindowHours <= 0) return flights;

    const cutoff = currentTime.valueOf() + timeWindowHours * 60 * 60 * 1000;
    return flights.filter(f => {
      const scheduledMs = new Date(f.scheduledTravelTime).getTime();
      return !Number.isFinite(scheduledMs) || scheduledMs <= cutoff;
    });
  }, [flights, currentTime, device.cluster?.settings?.flightTimeWindowInHour]);

  // Periodic refetch — ensures flights entering the backend's time window are picked up
  useEffect(() => {
    if (!device.cluster?.settings?.flightTimeWindowInHour) return;

    const intervalId = setInterval(() => {
      fetchFlights();
    }, 60_000);

    return () => clearInterval(intervalId);
  }, [device.cluster?.settings?.flightTimeWindowInHour, fetchFlights]);

  const isFullScreenAd =
    !!currentAd &&
    !currentAd.ratio &&
    currentAd.advertisementType === AdvertisementType.FullScreen &&
    currentAd.type === 'ad'

  const hours = currentTime.utcOffset(8).format('HH')
  const minutes = currentTime.utcOffset(8).format('mm')
  const dateStr = currentTime.utcOffset(8).format('MMM DD, ddd')

  const enableNextFlight = device.cluster?.settings?.enableNextFlight
  const clusterDefaultImage = device.cluster?.settings?.defaultDisplayImage
    ? `${ADS_URL}/cluster-settings/${device.cluster.settings.defaultDisplayImage}`
    : null

  const renderFlights = () => {
    if (!initialLoadDone) return null

    if (!windowedFlights.length || !device.assignment) {
      return (
        <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', background: '#000' }}>
          <div className="fids-header">
            <span className="fids-header-label">
              <span className="fids-header-label-dim">GATE</span>{' '}
              <span className="fids-header-label-bold">{device.assignment ?? '—'}</span>
            </span>
            <div className="fids-header-datetime">
              <span className="fids-header-date">{dateStr}</span>
            </div>
          </div>
          <div style={{ flex: 1, position: 'relative', minHeight: 0 }}>
            {clusterDefaultImage ? (
              <img
                src={clusterDefaultImage}
                alt="Default display"
                style={{
                  position: 'absolute',
                  inset: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'fill',
                }}
              />
            ) : (
              DEFAULT_IMAGES.map((src, i) => (
                <img
                  key={src}
                  src={src}
                  alt="Default display"
                  style={{
                    position: 'absolute',
                    inset: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'fill',
                    opacity: i === imgIndex ? 1 : 0,
                    transition: 'opacity 1s ease-in-out',
                  }}
                />
              ))
            )}
          </div>
          <div className="fids-footer">
            {device.announcement?.messageContent?.lines?.length ? (
              <div className="fids-footer-marquee">
                <ScrollingText
                  sentences={device.announcement.messageContent.lines}
                  speed={device.announcement.messageContent.speed || 50}
                  separator=" &bull; "
                  backgroundColor={device.announcement.messageContent.backgroundColor ?? 'transparent'}
                  textColor={device.announcement.messageContent.textColor ?? 'white'}
                  fontFamily={device.announcement.messageContent.fontFamily ?? 'Arial, sans-serif'}
                  fontSize={device.announcement.messageContent.fontSize ?? 16}
                  noScroll={device.announcement.messageContent.noScroll ?? false}
                />
              </div>
            ) : (
              <div className="fids-footer-spacer" />
            )}
            <div className="fids-footer-divider" />
            <div className="fids-footer-logo">
              <img src={mahbLogo} alt="MAHB Logo" className="fids-footer-logo-img" />
            </div>
          </div>
        </div>
      )
    }

    if (windowedFlights.length === 1) {
      return (
        <SingleFlightBoarding
          gate={device.assignment}
          flight={windowedFlights[0]}
          isNext={windowedFlights[0].isNext}
          enableNextFlight={enableNextFlight}
          announcement={device.announcement}
        />
      )
    }

    return (
      <DoubleFlightBoarding
        gate={device.assignment}
        flights={[windowedFlights[0], windowedFlights[1]]}
        enableNextFlight={enableNextFlight}
        announcement={device.announcement}
      />
    )
  }

  return (
    <div className="flight-board-container container-fluid p-0">
      {isFullScreenAd ? (
        <AdvertisementDisplay advertisement={currentAd} isFullScreen={true} />
      ) : (
        <>
          {renderFlights()}
          {currentAd?.ratio && (
            <AdvertisementDisplay advertisement={currentAd} isFullScreen={false} />
          )}
        </>
      )}
    </div>
  )
}

export default FlightBoardingPage